// Executive Live Review (approved HTML:
// 12-Executive-Live-Consistency-Review-v4.html).
//
// Executive rail. Blocked for Sales reps via the role gate. Period
// toggle, scorecard, five business cards and four right-rail cards per
// the approved layout in preview mode (?preview=sarah or dev-mock).
//
// W37b (2026-10-20): on a real session the screen reads
// GET /v1/executive/summary (W37a, sinks/executive-sink.mjs, READ;
// MEASURED for the url-bootstrap session of user 89608213: `{kpis:
// {callsToday, notesToday, tasksToday, repsTotal: 10, ...}, notAvailable:
// {sales, subscriptions, retention, service}}`; sales_rep sessions get
// 403 MANAGER_ROLE_REQUIRED). It renders the real KPI tiles (HubSpot
// engagement counts for today), the per-rep rollup, and one honest
// "not available" card per roll-up the server reports as missing a
// source (Shopify / Recharge / Zoho keys are not on the api-server, per
// the server's own notes). The "Sample data" fixture never renders on a
// real session.
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { useManagerRead } from '../TeamLive/components/useManagerRead.js';
import { NotAvailableCard } from '../TeamLive/components/NotAvailableCard.js';
import { RepRollupTable, formatSnapshot, mapRepRollup, type RepRollupRow } from '../TeamLive/components/RepRollupTable.js';
import { ExecutiveHeader, type PeriodMode } from './components/ExecutiveHeader.js';
import { ExecutiveScorecard } from './components/ExecutiveScorecard.js';
import { BusinessResultsTrend } from './components/BusinessResultsTrend.js';
import { SubscriptionHealth } from './components/SubscriptionHealth.js';
import { SalesEffectiveness } from './components/SalesEffectiveness.js';
import { CustomerRetentionGrowth } from './components/CustomerRetentionGrowth.js';
import { ServicePerformance } from './components/ServicePerformance.js';
import { ExecutiveAttention } from './components/ExecutiveAttention.js';
import { ExecutiveInsights } from './components/ExecutiveInsights.js';
import { SourceConfidence } from './components/SourceConfidence.js';
import { ExecutiveProgramPulse } from './components/ExecutiveProgramPulse.js';
import {
  PERIOD_NOTE,
  SNAPSHOT_AT,
  SCORECARDS,
  TREND_MONTHS,
  MTD_RESULT,
  SUBSCRIPTION_TILES,
  SUBSCRIPTION_ROWS,
  EFFECTIVENESS_ROWS,
  CUSTOMER_HEALTH,
  SERVICE_HEALTH,
  ATTENTION_ITEMS,
  INSIGHTS_ROWS,
  SOURCE_CONFIDENCE,
  PROGRAM_PULSE,
  type Scorecard,
} from './mockData.js';
import './styles.css';

export interface ExecutiveLiveScreenProps {
  stateOverride?: BoundaryState;
}

// Shape of the design fixture the preview path renders.
interface ExecutiveLivePayload {
  periodNote: typeof PERIOD_NOTE;
  snapshotAt: typeof SNAPSHOT_AT;
  scorecards: typeof SCORECARDS;
  trendMonths: typeof TREND_MONTHS;
  mtdResult: typeof MTD_RESULT;
  subscriptionTiles: typeof SUBSCRIPTION_TILES;
  subscriptionRows: typeof SUBSCRIPTION_ROWS;
  effectivenessRows: typeof EFFECTIVENESS_ROWS;
  customerHealth: typeof CUSTOMER_HEALTH;
  serviceHealth: typeof SERVICE_HEALTH;
  attentionItems: typeof ATTENTION_ITEMS;
  insightsRows: typeof INSIGHTS_ROWS;
  sourceConfidence: typeof SOURCE_CONFIDENCE;
  programPulse: typeof PROGRAM_PULSE;
}

const EXECUTIVE_FIXTURE: ExecutiveLivePayload = {
  periodNote: PERIOD_NOTE,
  snapshotAt: SNAPSHOT_AT,
  scorecards: SCORECARDS,
  trendMonths: TREND_MONTHS,
  mtdResult: MTD_RESULT,
  subscriptionTiles: SUBSCRIPTION_TILES,
  subscriptionRows: SUBSCRIPTION_ROWS,
  effectivenessRows: EFFECTIVENESS_ROWS,
  customerHealth: CUSTOMER_HEALTH,
  serviceHealth: SERVICE_HEALTH,
  attentionItems: ATTENTION_ITEMS,
  insightsRows: INSIGHTS_ROWS,
  sourceConfidence: SOURCE_CONFIDENCE,
  programPulse: PROGRAM_PULSE,
};

/** One roll-up the server reports as having no composed source. */
export interface NotAvailableSection {
  key: 'sales' | 'subscriptions' | 'retention' | 'service';
  reason: string;
  note: string | null;
}

/** Real summary, fields READ from sinks/executive-sink.mjs. */
export interface ExecutiveSummary {
  snapshotAt: string | null;
  windowLabel: string | null;
  kpis: {
    callsToday: number;
    notesToday: number;
    tasksCreatedToday: number;
    touchesToday: number;
    repsActiveToday: number;
    repsTotal: number;
  };
  byRep: RepRollupRow[];
  notAvailable: NotAvailableSection[];
  truncated: boolean;
  viewerRole: string | null;
}

const SECTION_TITLES: Record<NotAvailableSection['key'], string> = {
  sales: 'Company sales',
  subscriptions: 'Subscription health',
  retention: 'Customer retention and growth',
  service: 'Customer service performance',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

/** Map the 200 body `{result, summary}` defensively; null when `summary` is absent. */
export function mapExecutiveSummary(body: unknown): ExecutiveSummary | null {
  const summary = isRecord(body) && isRecord(body.summary) ? body.summary : null;
  if (!summary) return null;
  const kpis = isRecord(summary.kpis) ? summary.kpis : {};
  const win = isRecord(summary.window) ? summary.window : {};
  const viewer = isRecord(summary.viewer) ? summary.viewer : {};
  const na = isRecord(summary.notAvailable) ? summary.notAvailable : {};
  const notAvailable: NotAvailableSection[] = [];
  for (const key of ['sales', 'subscriptions', 'retention', 'service'] as const) {
    const entry = isRecord(na[key]) ? na[key] : null;
    if (!entry) continue;
    if (entry.available === true) continue;
    notAvailable.push({ key, reason: readString(entry.reason) ?? 'NOT_AVAILABLE', note: readString(entry.note) });
  }
  const byRep = mapRepRollup(summary.byRep);
  return {
    snapshotAt: readString(summary.snapshotAt),
    windowLabel: readString(win.label),
    kpis: {
      callsToday: readCount(kpis.callsToday),
      notesToday: readCount(kpis.notesToday),
      tasksCreatedToday: readCount(kpis.tasksCreatedToday ?? kpis.tasksToday),
      touchesToday: readCount(kpis.touchesToday),
      repsActiveToday: readCount(kpis.repsActiveToday),
      repsTotal: typeof kpis.repsTotal === 'number' ? readCount(kpis.repsTotal) : byRep.length,
    },
    byRep,
    notAvailable,
    truncated: summary.truncated === true,
    viewerRole: readString(viewer.role),
  };
}

/** KPI tiles; every value is read off the body (no delta: one window only). */
export function liveScorecards(summary: ExecutiveSummary): Scorecard[] {
  const caption = `HubSpot engagements · ${summary.windowLabel ?? 'today'}`;
  const k = summary.kpis;
  return [
    { key: 'calls-today', label: 'CALLS TODAY', value: String(k.callsToday), delta: '', caption },
    { key: 'notes-today', label: 'NOTES TODAY', value: String(k.notesToday), delta: '', caption },
    { key: 'tasks-today', label: 'TASKS CREATED TODAY', value: String(k.tasksCreatedToday), delta: '', caption },
    { key: 'touches-today', label: 'TOUCHES TODAY', value: String(k.touchesToday), delta: '', caption },
    {
      key: 'reps-active',
      label: 'REPS ACTIVE TODAY',
      value: `${k.repsActiveToday} of ${k.repsTotal}`,
      delta: '',
      caption: 'Owners with at least one engagement today',
    },
    { key: 'reps-total', label: 'HUBSPOT OWNERS', value: String(k.repsTotal), delta: '', caption: 'Non-archived owners' },
  ];
}

export function ExecutiveLiveScreen({
  stateOverride,
}: ExecutiveLiveScreenProps = {}): ReactNode {
  const [mode, setMode] = useState<PeriodMode>('mtd');
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const read = useManagerRead<unknown>({ path: '/v1/executive/summary', enabled: !stateOverride });
  const preview = read.status === 'preview';
  const summary = useMemo(
    () => (read.status === 'ready' ? mapExecutiveSummary(read.body) : null),
    [read],
  );

  const liveState: BoundaryState | null = (() => {
    if (stateOverride || preview) return null;
    switch (read.status) {
      case 'pending':
      case 'loading':
        return { kind: 'loading', label: 'Loading Executive Live…' };
      case 'forbidden':
        return {
          kind: 'blocked',
          title: 'Executive Live is restricted',
          reason: read.detail ?? `Manager or executive role required for this surface; your session role is ${read.role ?? 'unknown'}.`,
          gateName: read.errorCode,
        };
      case 'unauthorized':
        return {
          kind: 'blocked',
          title: 'Session unavailable',
          reason: read.detail ?? 'Open the Sales Workspace from a HubSpot record and retry.',
          gateName: read.errorCode,
        };
      case 'error':
        return {
          kind: 'error',
          title: 'Executive Live could not load',
          detail: read.detail ?? `Request failed with status ${read.httpStatus}.`,
          errorCode: read.errorCode,
        };
      case 'ready':
        if (!summary) {
          return { kind: 'empty', title: 'No published period yet', body: 'The summary body carried no snapshot.' };
        }
        return null;
      default:
        return null;
    }
  })();

  const state: BoundaryState =
    stateOverride ?? liveState ?? (preview ? resolveState(kind, undefined) : { kind: 'default' });

  if (!preview && !stateOverride) {
    return (
      <WorkspaceChrome screen="executive-live" rail="manager">
        <div className="exec-breadcrumb">
          <div>
            Workspace / <strong>Executive Live</strong>
          </div>
          <span data-testid="executive-live-mode">Live · HubSpot engagements · no sales, subscription, retention or service source yet</span>
        </div>
        <DataBoundary state={state} testId="executive-live-boundary">
          {summary ? (
            <>
              <ExecutiveHeader
                mode={mode}
                onModeChange={setMode}
                periodNote={summary.windowLabel ?? 'today'}
                showPeriodToggle={false}
              />
              <ExecutiveScorecard cards={liveScorecards(summary)} />
              <div className="exec-grid">
                <div className="exec-stack">
                  {summary.notAvailable.map(section => (
                    <NotAvailableCard
                      key={section.key}
                      title={SECTION_TITLES[section.key]}
                      reason={section.reason}
                      note={section.note}
                      dataComponent={`executive-${section.key}-unavailable`}
                    />
                  ))}
                </div>
                <aside className="exec-stack">
                  <RepRollupTable
                    reps={summary.byRep}
                    title="Sales activity by rep"
                    subtitle={`${summary.kpis.repsTotal} HubSpot owner${summary.kpis.repsTotal === 1 ? '' : 's'} · ${summary.windowLabel ?? 'today'}`}
                    dataComponent="executive-by-rep"
                  />
                  {summary.truncated ? (
                    <p className="exec-note" data-testid="executive-truncated">
                      Counts are a partial read: HubSpot returned more engagements than one refresh scans.
                    </p>
                  ) : null}
                </aside>
              </div>
              <div className="exec-footer-note">
                <span>HubSpot engagements for {summary.windowLabel ?? 'today'} · other roll-ups not available yet</span>
                <span id="exec-snapshot">{formatSnapshot(summary.snapshotAt)}</span>
              </div>
            </>
          ) : null}
        </DataBoundary>
      </WorkspaceChrome>
    );
  }

  const payload = EXECUTIVE_FIXTURE;
  return (
    <WorkspaceChrome screen="executive-live" rail="manager">
      <div className="exec-breadcrumb">
        <div>
          Workspace / <strong>Executive Live</strong>
        </div>
        <span>Design review #12 · Sample data</span>
      </div>
      <DataBoundary state={state} testId="executive-live-boundary">
        <ExecutiveHeader mode={mode} onModeChange={setMode} periodNote={payload.periodNote} />
        <ExecutiveScorecard cards={payload.scorecards} />
        <div className="exec-grid">
          <div className="exec-stack">
            <BusinessResultsTrend
              months={payload.trendMonths}
              mtdValue={payload.mtdResult.value}
              mtdCaption={payload.mtdResult.caption}
              lines={payload.mtdResult.lines}
            />
            <SubscriptionHealth tiles={payload.subscriptionTiles} rows={payload.subscriptionRows} />
            <SalesEffectiveness rows={payload.effectivenessRows} />
            <CustomerRetentionGrowth stats={payload.customerHealth} />
            <ServicePerformance stats={payload.serviceHealth} snapshot={payload.snapshotAt} />
          </div>
          <aside className="exec-stack">
            <ExecutiveAttention items={payload.attentionItems} />
            <ExecutiveInsights rows={payload.insightsRows} />
            <SourceConfidence rows={payload.sourceConfidence} />
            <ExecutiveProgramPulse rows={payload.programPulse} />
          </aside>
        </div>
        <div className="exec-footer-note">
          <span>Sample data and sample targets · Design review #12 of 22</span>
          <span id="exec-snapshot">{payload.snapshotAt}</span>
        </div>
      </DataBoundary>
      {!stateOverride ? (
        <StatePreview kind={kind} onChange={setKind} />
      ) : null}
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return { kind: 'loading', label: 'Loading Executive Live…' };
    case 'empty':
      return {
        kind: 'empty',
        title: 'No published period yet',
        body: 'The current period has not reported. The last confirmed snapshot still appears above when the feed catches up.',
      };
    case 'error':
      return {
        kind: 'error',
        title: 'Executive Live could not load',
        detail: 'The company scorecard feed did not respond.',
        errorCode: 'EXECUTIVE_LIVE_UNAVAILABLE',
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'subscription-health', reason: 'subscription_feed_stale' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: 'Executive Live is restricted',
        reason: 'Executive role required for this surface.',
        gateName: 'EXECUTIVE_ROLE_REQUIRED',
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: 'Refreshing scorecard…',
        actionName: 'executive_scorecard_refresh',
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StatePreview({
  kind,
  onChange,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
}): ReactNode {
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 16,
        padding: 10,
        border: '1px dashed #c4d4e5',
        borderRadius: 6,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

export default ExecutiveLiveScreen;
